import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { setDocumentTitle } from '../../utils/documentTitle';
import StoreCard from './StoreCard';
import Pagination from './Pagination';
import BottomNavigation from '../Layout/BottomNavigation';
import LevelBadge from './LevelBadge';
import VerificationBadge, { RiskScoreBadge } from './VerificationBadge';

const StoresList = () => {
  const { t } = useTranslation();
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('popularity'); // popularity, rating, newest, name
  const [viewMode, setViewMode] = useState('grid'); // grid, list
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [showFilterModal, setShowFilterModal] = useState(false);

  // Categories
  const categories = [
    { id: 'all', name: t('marketplace.all'), icon: '🏪' },
    { id: 'Flowers', name: t('marketplace.flowers'), icon: '🌿' },
    { id: 'Seeds', name: t('marketplace.seeds'), icon: '🌱' },
    { id: 'Edibles', name: t('marketplace.edibles'), icon: '🍪' },
    { id: 'Pre Rolls', name: t('marketplace.preRolls'), icon: '🚬' },
    { id: 'Cannabis Oils', name: t('marketplace.cannabisOils'), icon: '💧' },
    { id: 'CBD Products', name: t('marketplace.cbdProducts'), icon: '🌿' },
    { id: 'Accessories', name: t('marketplace.accessories', { defaultValue: 'อุปกรณ์' }), icon: '🔧' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'popularity', label: 'ยอดนิยม', icon: '🔥' },
    { value: 'rating', label: 'คะแนนสูงสุด', icon: '⭐' },
    { value: 'newest', label: 'ใหม่ล่าสุด', icon: '🆕' },
    { value: 'name', label: 'ชื่อ A-Z', icon: '🔤' }
  ];

  useEffect(() => {
    setDocumentTitle('ร้านค้าทั้งหมด - CannaTrade');
    fetchStores();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [stores, searchTerm, selectedCategory, sortBy]);

  const fetchStores = async () => {
    setLoading(true);
    try {
      let storesQuery;
      
      // Query stores based on sort
      if (sortBy === 'popularity') {
        try {
          storesQuery = query(
            collection(db, 'stores'),
            where('status', '==', 'active'),
            orderBy('reputationScore', 'desc'),
            limit(100)
          );
        } catch (error) {
          // Fallback if index doesn't exist
          storesQuery = query(
            collection(db, 'stores'),
            where('status', '==', 'active'),
            limit(100)
          );
        }
      } else if (sortBy === 'newest') {
        try {
          storesQuery = query(
            collection(db, 'stores'),
            where('status', '==', 'active'),
            orderBy('createdAt', 'desc'),
            limit(100)
          );
        } catch (error) {
          storesQuery = query(
            collection(db, 'stores'),
            where('status', '==', 'active'),
            limit(100)
          );
        }
      } else {
        storesQuery = query(
          collection(db, 'stores'),
          where('status', '==', 'active'),
          limit(100)
        );
      }

      const snapshot = await getDocs(storesQuery);
      let storesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Manual sort for rating and name
      if (sortBy === 'rating') {
        storesData.sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
      } else if (sortBy === 'name') {
        storesData.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'th'));
      } else if (sortBy === 'popularity' && !snapshot.query.orderBy) {
        // Fallback sort by reputation score
        storesData.sort((a, b) => (b.reputationScore || 0) - (a.reputationScore || 0));
      }

      setStores(storesData);
    } catch (error) {
      console.error('Error fetching stores:', error);
      setStores([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...stores];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(store => 
        store.name?.toLowerCase().includes(searchLower) ||
        store.description?.toLowerCase().includes(searchLower) ||
        store.category?.toLowerCase().includes(searchLower)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(store => store.category === selectedCategory);
    }

    setFilteredStores(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination
  const totalPages = Math.ceil(filteredStores.length / itemsPerPage);
  const paginatedStores = filteredStores.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Stats
  const totalStores = filteredStores.length;
  const verifiedStores = filteredStores.filter(s => s.verification?.status === 'verified').length;
  const averageRating = filteredStores.length > 0
    ? (filteredStores.reduce((sum, s) => sum + (s.averageRating || 0), 0) / filteredStores.length).toFixed(1)
    : '0.0';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดร้านค้า...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">ร้านค้าทั้งหมด</h1>
          <p className="text-sm md:text-base text-gray-600">
            ค้นหาและสำรวจร้านค้าคุณภาพใน CannaTrade
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6">
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-green-600">{totalStores}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">ร้านค้าทั้งหมด</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-blue-600">{verifiedStores}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">ร้านค้า Verified</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-yellow-600">{averageRating}</div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">คะแนนเฉลี่ย</div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center">
            <div className="text-2xl md:text-3xl font-bold text-purple-600">
              {filteredStores.filter(s => s.reputationLevel && s.reputationLevel >= 3).length}
            </div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">ร้านค้า Gold+</div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          {/* Search Bar */}
          <div className="mb-4">
            <div className="relative">
              <input
                type="text"
                placeholder="ค้นหาร้านค้า..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 md:py-3 pl-10 md:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <svg
                className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Category Filter - Horizontal Scroll on Mobile */}
          <div className="mb-4">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                    selectedCategory === category.id
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span>{category.icon}</span>
                  <span className="text-sm md:text-base">{category.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Sort and View Toggle */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
            {/* Sort Options */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm md:text-base text-gray-600">เรียงตาม:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {sortOptions.map(option => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      fetchStores();
                    }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                      sortBy === option.value
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{option.icon}</span>
                    <span>{option.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="Grid View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg transition-colors ${
                  viewMode === 'list'
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                title="List View"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Results Count */}
        <div className="mb-4 text-sm md:text-base text-gray-600">
          พบ {totalStores} ร้านค้า
        </div>

        {/* Stores Grid/List */}
        {paginatedStores.length > 0 ? (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 mb-6">
                {paginatedStores.map(store => (
                  <StoreCard key={store.id} store={store} />
                ))}
              </div>
            ) : (
              <div className="space-y-2 md:space-y-3 mb-6">
                {paginatedStores.map(store => (
                  <Link 
                    key={store.id} 
                    to={`/marketplace/stores/${store.id}`} 
                    className="block"
                  >
                    <div className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow border border-gray-100">
                      <div className="flex gap-3 md:gap-4 p-3 md:p-4">
                        {/* Store Image - Compact */}
                        <div className="relative w-20 h-20 md:w-24 md:h-24 flex-shrink-0 bg-gradient-to-br from-green-400 to-green-600 rounded-lg overflow-hidden">
                          {store.coverImage ? (
                            <img
                              src={store.coverImage}
                              alt={store.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-white text-xl md:text-2xl">
                              🏪
                            </div>
                          )}
                          {/* Logo overlay */}
                          <div className="absolute bottom-0 right-0 transform translate-x-1/4 translate-y-1/4">
                            {store.logo ? (
                              <img
                                src={store.logo}
                                alt={store.name}
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white object-cover"
                                onError={(e) => {
                                  e.target.src = '/logo.png';
                                }}
                              />
                            ) : (
                              <img
                                src="/logo.png"
                                alt="CannaTrade Logo"
                                className="w-8 h-8 md:w-10 md:h-10 rounded-full border-2 border-white object-contain bg-white p-0.5"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* Store Info - Compact */}
                        <div className="flex-1 min-w-0 flex flex-col justify-between">
                          <div className="flex-1">
                            {/* Store Name */}
                            <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-1 line-clamp-1">
                              {store.name}
                            </h3>
                            
                            {/* Badges Row - Level and Category */}
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {store.reputationLevel && (
                                <div className="scale-75 md:scale-90 origin-left">
                                  <LevelBadge level={store.reputationLevel} score={store.reputationScore} type="store" />
                                </div>
                              )}
                              {store.category && (
                                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                                  {store.category}
                                </span>
                              )}
                            </div>

                            {/* Description - Hidden on mobile, shown on desktop */}
                            {store.description && (
                              <p className="hidden md:block text-sm text-gray-600 mb-2 line-clamp-1">
                                {store.description}
                              </p>
                            )}
                          </div>

                          {/* Verification, Risk Score, and Stats - All in one row */}
                          <div className="flex items-center gap-1.5 md:gap-2 flex-wrap text-xs md:text-sm">
                            {/* Verification Badge */}
                            {store.verification ? (
                              <div className="scale-75 md:scale-90 origin-center">
                                <VerificationBadge verification={store.verification} size="sm" />
                              </div>
                            ) : (
                              <div className="scale-75 md:scale-90 origin-center">
                                <VerificationBadge verification={null} size="sm" />
                              </div>
                            )}
                            
                            {/* Risk Score Badge */}
                            {store.verification?.riskScore !== undefined && (
                              <div className="scale-75 md:scale-90 origin-center">
                                <RiskScoreBadge riskScore={store.verification.riskScore} size="sm" />
                              </div>
                            )}

                            {/* Stats - Orders */}
                            <div className="text-gray-600">
                              <span className="font-semibold">{store.completedOrders || 0}</span>
                              <span className="ml-0.5">ออเดอร์</span>
                            </div>

                            {/* Stats - Rating */}
                            <div className="flex items-center gap-0.5 text-gray-600">
                              <span className="font-semibold">
                                {store.averageRating?.toFixed(1) || '0.0'}
                              </span>
                              <span className="text-yellow-500">★</span>
                              {store.totalReviews > 0 && (
                                <span className="text-gray-500">({store.totalReviews})</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6 mb-20 md:mb-6">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={setCurrentPage}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredStores.length}
                  onItemsPerPageChange={(newItemsPerPage) => {
                    setItemsPerPage(newItemsPerPage);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-20 md:mb-6">
            <div className="text-6xl mb-4">🏪</div>
            <p className="text-lg md:text-xl text-gray-800 mb-2">ไม่พบร้านค้า</p>
            <p className="text-sm md:text-base text-gray-600">
              ลองเปลี่ยนคำค้นหาหรือหมวดหมู่ดูนะครับ
            </p>
          </div>
        )}
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation />
    </div>
  );
};

export default StoresList;

